import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CalendarX, Search } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { api, fmtErr } from "../lib/api";

const STATUS_STYLES = {
  confirmed: "bg-emerald-400/10 text-emerald-300 border-emerald-400/30",
  seated: "bg-blue-400/10 text-blue-300 border-blue-400/30",
  completed: "bg-zinc-400/10 text-zinc-300 border-zinc-400/30",
  cancelled: "bg-red-400/10 text-red-300 border-red-400/30",
  no_show: "bg-amber-400/10 text-amber-300 border-amber-400/30",
  pending: "bg-orange-400/10 text-orange-300 border-orange-400/30",
};

export default function FindBooking() {
  const [params] = useSearchParams();
  const [ref, setRef] = useState(params.get("ref") || "");
  const [email, setEmail] = useState(params.get("email") || "");
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const lookup = async (r = ref, e = email) => {
    if (!r || !e) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/bookings/lookup", { params: { ref: r, email: e } });
      setBooking(data);
    } catch (err) {
      setBooking(null);
      setError(fmtErr(err));
    }
    setLoading(false);
  };

  useEffect(() => {
    if (params.get("ref") && params.get("email")) lookup(params.get("ref"), params.get("email"));
  }, []);

  const cancel = async () => {
    try {
      await api.post(`/bookings/${booking.id}/cancel`, null, { params: { email: booking.email } });
      toast.success("Booking cancelled");
      lookup();
    } catch (err) {
      toast.error(fmtErr(err));
    }
  };

  const cancellable = booking && booking.status === "confirmed" && new Date(`${booking.date}T${booking.time}:00`).getTime() > Date.now();

  return (
    <div data-testid="find-booking-page" className="max-w-xl mx-auto px-6 py-20 min-h-screen">
      <p className="text-xs tracking-[0.3em] uppercase text-primary mb-3">Guest access</p>
      <h1 className="font-display text-4xl tracking-tight">Find your booking</h1>
      <p className="text-muted-foreground mt-3 text-sm">Enter the reference from your confirmation and the email you booked with.</p>

      <div className="mt-8 space-y-4">
        <div>
          <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Booking reference</Label>
          <Input data-testid="lookup-ref-input" value={ref} onChange={(e) => setRef(e.target.value)}
            placeholder="e.g. a41cb993" className="mt-1.5 bg-card border-border/60" />
        </div>
        <div>
          <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Email</Label>
          <Input data-testid="lookup-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com" className="mt-1.5 bg-card border-border/60"
            onKeyDown={(e) => e.key === "Enter" && lookup()} />
        </div>
        <Button data-testid="lookup-btn" onClick={() => lookup()} disabled={loading || !ref || !email} className="rounded-full">
          <Search className="w-4 h-4 mr-2" /> {loading ? "Searching..." : "Find booking"}
        </Button>
        {error && <p data-testid="lookup-error" className="text-sm text-destructive">{error}</p>}
      </div>

      {booking && (
        <div data-testid="lookup-result" className="mt-8 rounded-2xl border border-border/60 bg-card p-6">
          <div className="flex items-center gap-3">
            <h3 className="font-display text-xl">{booking.restaurant_name}</h3>
            <Badge variant="outline" className={STATUS_STYLES[booking.status]}>{booking.status.replace("_", " ")}</Badge>
          </div>
          <p className="text-sm text-muted-foreground mt-2">
            {booking.date} at {booking.time} · Party of {booking.party_size} · Table {booking.table_label}
          </p>
          {cancellable && (
            <Button data-testid="guest-cancel-btn" variant="outline" size="sm" className="rounded-full mt-5" onClick={cancel}>
              <CalendarX className="w-4 h-4 mr-2" /> Cancel this booking
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
